import LoadMoreButton from '@atoms/LoadMoreButton/LoadMoreButton';
import { IKitBag } from '@models/IKitBag';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useRefocus from '@utils/hooks/useRefocus';
import classNames from 'classnames';
import { useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import KitBagCard from './KitBagCard';

const KitBag = ({ title, loadMoreLabel, loadLessLabel, items }: IKitBag) => {
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery(1440);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const focusPlaceholderRef = useRefocus<HTMLAnchorElement>(open);

  return (
    <SoftLoadWrapper>
      <section className="overflow-hidden text-black-100 dark:text-white">
        {title && !isMobile && (
          <p className="body-one container text-black-100 xl:mx-0 xl:max-w-none xl:px-0 dark:text-grey-400">
            {title}
          </p>
        )}

        {!!items?.length && (
          <div
            className={classNames(
              'flex w-full flex-col overflow-hidden xl:mt-4 xl:gap-5',
              {
                'mt-6': !isMobile,
              }
            )}
          >
            <div className="scrollbar-hide relative w-full snap-x overflow-x-auto">
              {isMobile ? (
                <NavigationCarousel
                  id="kitbag-carousel"
                  items={items}
                  title={title}
                  titleClassName="body-one text-black-100 dark:text-grey-400"
                  spaceBetween={16}
                  loop={false}
                  isSoftLoadAnimationDisabled
                >
                  {(itemsToDisplay) =>
                    itemsToDisplay?.map((item, index) => (
                      <SwiperSlide className="stretch-card" key={item.name}>
                        <KitBagCard
                          item={item}
                          index={index}
                          focusedIndex={focusedIndex}
                          focusPlaceholderRef={focusPlaceholderRef}
                        />
                      </SwiperSlide>
                    ))
                  }
                </NavigationCarousel>
              ) : (
                <div className="flex w-full flex-row overflow-visible pl-5 md:px-15 lg:px-24 xl:flex-col xl:gap-5 xl:px-0">
                  {items
                    .slice(
                      0,
                      open || !loadMoreLabel || !loadLessLabel
                        ? items.length
                        : 3
                    )
                    .map((item, index) => (
                      <KitBagCard
                        key={item.name}
                        item={item}
                        index={index}
                        focusedIndex={focusedIndex}
                        focusPlaceholderRef={focusPlaceholderRef}
                        wrapWithContainer
                        desktopWrapperClassName={classNames(
                          'h-auto shrink-0 snap-center xl:h-36 xl:w-full xl:pr-0',
                          items.length === 1 ? 'w-full' : 'max-w-[80vw]',
                          index + 1 === items.length
                            ? 'pr-5 md:pr-15 lg:pr-24'
                            : 'pr-4'
                        )}
                      />
                    ))}
                </div>
              )}

              <div
                className={classNames(
                  'pointer-events-none absolute bottom-0 left-0 z-10 hidden h-36 w-full bg-gradient-to-t from-white to-transparent dark:from-black-100',
                  {
                    'xl:block':
                      !open &&
                      loadMoreLabel &&
                      loadLessLabel &&
                      items.length > 3,
                  }
                )}
              />
            </div>

            {items.length > 3 &&
              loadMoreLabel &&
              loadLessLabel &&
              !isMobile && (
                <LoadMoreButton
                  loadLessLabel={loadLessLabel}
                  loadMoreLabel={loadMoreLabel}
                  open={open}
                  onClick={() => {
                    setOpen(!open);
                    setFocusedIndex(open ? 2 : 3);
                  }}
                />
              )}
          </div>
        )}
      </section>
    </SoftLoadWrapper>
  );
};

export default KitBag;
